// Admin -> Dashboard: client of GET /api/admin/dashboard (documentation/protocol.md "Admin
// Dashboard") and the browser-side rules around it. A section is null when the caller lacks the
// permission for it: render it as "not available", never as 0.

// Admin sidebar sections in display order; the Dashboard is first and the default.
export const ADMIN_SECTIONS = ['Dashboard', 'General', 'Radios', 'Groups', 'Users', 'ActivityLogs', 'Log', 'Recordings', 'Conversations'] as const;
export type AdminSection = (typeof ADMIN_SECTIONS)[number];
export const DEFAULT_ADMIN_SECTION: AdminSection = 'Dashboard';

// The section a /Admin/:section URL shows; null for an unknown one (the legacy /Admin/Accounts
// included), which is redirected to the Dashboard instead of rendering an empty page.
export const adminSectionFor = (section: string | undefined): AdminSection | null => {
  if (!section) return DEFAULT_ADMIN_SECTION;
  return (ADMIN_SECTIONS as readonly string[]).includes(section) ? (section as AdminSection) : null;
};

export type DashboardSectionScope = 'network' | 'departments';

export interface AdminDashboardData {
  network: { id: string; name: string };
  customer: { id: string; name: string };
  scope: { tenant_wide: boolean; departments: { id: string; name: string }[] };
  radios: {
    scope: DashboardSectionScope;
    total: number;
    connected: number;
    offline: number;
    transmitting: number;
    low_battery: number;
    blocked: number;
  } | null;
  groups: { scope: DashboardSectionScope; total: number } | null;
  network_accounts: { total: number; active: number; disabled: number } | null;
  sos: { scope: DashboardSectionScope; active: number } | null;
  generated_at: string;
}

// The selected network's numbers are refreshed this often (and on every network switch).
export const DASHBOARD_REFRESH_MS = 15000;

export class DashboardRequestError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export const getAdminDashboard = async (orgId: string, signal?: AbortSignal): Promise<AdminDashboardData> => {
  const response = await fetch('/api/admin/dashboard', { headers: { 'X-Org-ID': orgId }, cache: 'no-store', signal });
  if (!response.ok) {
    const message = response.status === 404
      ? 'This network is no longer available to you.'
      : response.status === 401
        ? 'Your session has ended. Sign in again.'
        : `Failed to load the dashboard (HTTP ${response.status}).`;
    throw new DashboardRequestError(message, response.status);
  }
  return response.json();
};

// The network to select from the authorized list: the current one while it is still in the list,
// else the remembered one if it is still in the list, else the first; '' for an empty list. A
// removed grant or a deleted network therefore never stays selected.
export const resolveNetworkSelection = (current: string, networks: { id: string }[], remembered: string | null): string => {
  if (current && networks.some((n) => n.id === current)) return current;
  if (remembered && networks.some((n) => n.id === remembered)) return remembered;
  return networks[0]?.id ?? '';
};

export interface DashboardState {
  orgId: string;
  data: AdminDashboardData | null;
  loading: boolean;
  error: string | null;
  // The last successful refresh failed: data (if any) is the previous answer.
  stale: boolean;
}

export const initialDashboardState = (orgId: string): DashboardState => ({ orgId, data: null, loading: !!orgId, error: null, stale: false });

interface Timers {
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
}

const realTimers: Timers = {
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (handle) => clearInterval(handle as ReturnType<typeof setInterval>),
};

// Polls one network's dashboard until stop(). Only the newest request may update the state: a
// slower earlier response, a response after stop() (network switch, unmount) or an answer for a
// different network is discarded, so stale numbers never replace the current selection's.
export function startDashboardPolling(
  orgId: string,
  onState: (state: DashboardState) => void,
  fetcher: (orgId: string, signal: AbortSignal) => Promise<AdminDashboardData> = getAdminDashboard,
  timers: Timers = realTimers,
  intervalMs = DASHBOARD_REFRESH_MS,
): () => void {
  let state = initialDashboardState(orgId);
  let seq = 0;
  let stopped = false;
  let inflight: AbortController | null = null;
  const emit = (next: DashboardState) => {
    state = next;
    onState(state);
  };
  if (!orgId) {
    emit(state);
    return () => {};
  }
  const load = async () => {
    const mine = ++seq;
    inflight?.abort();
    const controller = new AbortController();
    inflight = controller;
    try {
      const data = await fetcher(orgId, controller.signal);
      if (stopped || mine !== seq) return;
      if (data?.network?.id !== orgId) throw new Error('The server answered for a different network.');
      emit({ orgId, data, loading: false, error: null, stale: false });
    } catch (err) {
      if (stopped || mine !== seq) return;
      const gone = err instanceof DashboardRequestError && (err.status === 404 || err.status === 401 || err.status === 403);
      emit({
        orgId,
        // A network that is gone (or a session that ended) keeps no numbers on screen.
        data: gone ? null : state.data,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load the dashboard.',
        stale: !gone && !!state.data,
      });
    }
  };
  emit(state);
  void load();
  const handle = timers.setInterval(() => void load(), intervalMs);
  return () => {
    stopped = true;
    inflight?.abort();
    timers.clearInterval(handle);
  };
}
